import { Badge, Box, Flex, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { currencyFormat } from 'components/Number';
import { HelperTooltip } from 'components/Tooltip';
import { buildMonitoringScorecardViewModel } from './monitoringCompanyScore';
import {
  monitoringCovenantStripSummary,
  monitoringKpiPctChange,
  monitoringPctOfCap,
} from './monitoringHelpers';

function formatCompactCurrency(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return '—';
  }
  const n = Number(value);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    // 3dp keeps ~$1k resolution so $1,125,202 reads $1.125M (not $1.13M).
    const millions = (n / 1_000_000).toFixed(3).replace(/\.?0+$/, '');
    return `$${millions}M`;
  }
  if (abs >= 1_000) {
    return `$${(n / 1_000).toFixed(1)}K`;
  }
  return currencyFormat(n, { maximumFractionDigits: 0 });
}

function formatCount(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return '—';
  }
  return Math.round(Number(value)).toLocaleString('en-US');
}

function pctChangeBadge(pct) {
  if (pct == null || !Number.isFinite(pct)) {
    return null;
  }
  const positive = pct >= 0;
  return (
    <Badge
      colorScheme={positive ? 'green' : 'red'}
      variant="subtle"
      fontSize="xs"
    >
      {positive ? '+' : ''}
      {pct.toFixed(1)}%
    </Badge>
  );
}

function CompareRows({ current, prior, currentLabel, priorLabel, formatValue }) {
  return (
    <VStack align="stretch" spacing={1} mt={3} w="100%">
      <Flex justify="space-between">
        <Text fontSize="xs" color="gray.600">
          {currentLabel}
        </Text>
        <Text fontSize="xs" fontWeight="semibold">
          {formatValue(current)}
        </Text>
      </Flex>
      <Flex justify="space-between">
        <Text fontSize="xs" color="gray.600">
          {priorLabel}
        </Text>
        <Text fontSize="xs" fontWeight="semibold">
          {formatValue(prior)}
        </Text>
      </Flex>
    </VStack>
  );
}

function SummaryCard({ title, children }) {
  return (
    <Box
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      bg="white"
      p={4}
      minH="140px"
    >
      <Text fontSize="sm" color="gray.600" mb={2}>
        {title}
      </Text>
      {children}
    </Box>
  );
}

function CompareCard({
  title,
  value,
  prior,
  formatValue,
  currentLabel,
  priorLabel,
  forecast,
  tooltip,
  helpLink,
}) {
  const pct = monitoringKpiPctChange(value, prior);
  return (
    <Box
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      bg="white"
      p={4}
    >
      <Flex justify="space-between" align="flex-start" gap={2} mb={1}>
        <Flex align="center" gap={2}>
          <Text fontSize="sm" color="gray.600">
            {title}
          </Text>
          {tooltip || helpLink ? (
            <HelperTooltip content={tooltip} helpLink={helpLink} />
          ) : null}
          {forecast ? (
            <Badge colorScheme="blue" variant="subtle" fontSize="xs">
              FORECAST
            </Badge>
          ) : null}
        </Flex>
        {pctChangeBadge(pct)}
      </Flex>
      <Text fontSize="2xl" fontWeight="bold" lineHeight="1.2">
        {formatValue(value)}
      </Text>
      <CompareRows
        current={value}
        prior={prior}
        currentLabel={currentLabel}
        priorLabel={priorLabel}
        formatValue={formatValue}
      />
    </Box>
  );
}

const MARKETING_FORECAST_TOOLTIP =
  'Full-month marketing forecast from the Marketing spend chart: month-to-date actual spend plus predicted spend for the rest of the month (remaining days × average daily spend over recent days). Uses yesterday as the as-of date when Monitoring is open on today, so incomplete same-day spend does not skew funding or the KPI.';

const REVENUE_FORECAST_TOOLTIP =
  'Full-month cashflow forecast from the Sales Highlights chart (actual + prediction). Uses the same GetRecurringRevenue cashflow series and Sales as-of date (latest complete day / yesterday when Monitoring is open on today).';

/**
 * KPI strip above Company Parameters.
 */
export default function MonitoringKpiStrip({
  fetchResult,
  riskInputsDraft,
}) {
  if (!fetchResult) {
    return null;
  }

  const scoreView = buildMonitoringScorecardViewModel(
    fetchResult.cashflowScorecard || [],
    riskInputsDraft || fetchResult.riskInputs || {},
  );
  const rated = (scoreView.valueRows || []).filter((r) => r.score !== '—');
  const scorePct =
    fetchResult.riskScoreFinalPct != null
      ? Number(fetchResult.riskScoreFinalPct) * 100
      : null;
  const weighted =
    rated.length > 0
      ? rated.reduce((sum, r) => sum + Number(r.score || 0), 0) / rated.length
      : null;

  const covenants = monitoringCovenantStripSummary(
    fetchResult.resultRows,
    fetchResult.roasCohort?.rows,
  );

  const exposure = fetchResult.postFundingExposure;
  const maxExposure = fetchResult.dealConstraints?.maxExposure;
  const exposurePct = monitoringPctOfCap(exposure, maxExposure);

  const funding = fetchResult.requestedFundingAmount;
  const maxFunding = fetchResult.dealConstraints?.maxFundingAmount;
  const fundingPct = monitoringPctOfCap(funding, maxFunding);

  const strip = fetchResult.kpiStrip || {};
  const mtdCur = strip.mtdCurrentLabel || 'This month';
  const mtdPrior = strip.mtdPriorLabel || 'Last month';
  const outCur = strip.outlookCurrentLabel || 'Projected';
  const outPrior = strip.outlookPriorLabel || 'Prior actual';

  const failedList =
    covenants.failedNames?.length > 0
      ? covenants.failedNames.join(', ')
      : null;

  return (
    <Box mt={6}>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
        <SummaryCard title="Company Score">
          <Text fontSize="4xl" fontWeight="bold" lineHeight="1.1" color="gray.800">
            {scorePct != null ? `${Math.round(scorePct)}%` : '—'}
          </Text>
          <Text fontSize="md" fontWeight="semibold" color="gray.700" mt={1}>
            {fetchResult.riskScoreFinalLabel || '—'}
          </Text>
          <Text fontSize="xs" color="gray.600" mt={2}>
            Rated {rated.length} / {(scoreView.valueRows || []).length} metrics
          </Text>
          <Text fontSize="xs" color="gray.600">
            Weighted score {weighted != null ? weighted.toFixed(4) : '—'}
          </Text>
        </SummaryCard>

        <SummaryCard title="Covenants that passed">
          <Flex align="baseline" gap={3} flexWrap="wrap">
            <Text fontSize="2xl" fontWeight="bold">
              {covenants.total > 0
                ? `${covenants.passed} / ${covenants.total}`
                : '—'}
            </Text>
            {failedList ? (
              <Text fontSize="sm" color="red.600" fontWeight="medium">
                Failed: {failedList}
              </Text>
            ) : null}
          </Flex>
        </SummaryCard>

        <SummaryCard title="Post-Funding Exposure">
          <Text fontSize="2xl" fontWeight="bold">
            {formatCompactCurrency(exposure)}
          </Text>
          <Text fontSize="xs" color="gray.600" mt={2}>
            {exposurePct != null
              ? `${exposurePct.toFixed(0)}% of ${formatCompactCurrency(maxExposure)} limit`
              : '—'}
          </Text>
        </SummaryCard>

        <SummaryCard title="Requested Funding Amount">
          <Text fontSize="2xl" fontWeight="bold">
            {formatCompactCurrency(funding)}
          </Text>
          <Text fontSize="xs" color="gray.600" mt={2}>
            {fundingPct != null
              ? `${fundingPct.toFixed(0)}% of the ${currencyFormat(maxFunding, {
                  maximumFractionDigits: 0,
                })} max`
              : '—'}
          </Text>
        </SummaryCard>
      </SimpleGrid>

      <Text mt={6} mb={3} fontWeight="semibold" fontSize="sm">
        Month to Date — {mtdCur} vs {mtdPrior}
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
        <CompareCard
          title="Marketing Spend"
          value={strip.marketingSpendMtd?.current}
          prior={strip.marketingSpendMtd?.prior}
          formatValue={formatCompactCurrency}
          currentLabel="This month"
          priorLabel="Last month"
        />
        <CompareCard
          title="Revenue"
          value={strip.revenueMtd?.current}
          prior={strip.revenueMtd?.prior}
          formatValue={formatCompactCurrency}
          currentLabel="This month"
          priorLabel="Last month"
        />
        <CompareCard
          title="New Payers"
          value={strip.newPayersMtd?.current}
          prior={strip.newPayersMtd?.prior}
          formatValue={formatCount}
          currentLabel={mtdCur}
          priorLabel={mtdPrior}
        />
        <CompareCard
          title="Returning Payers"
          value={strip.returningPayersMtd?.current}
          prior={strip.returningPayersMtd?.prior}
          formatValue={formatCount}
          currentLabel={mtdCur}
          priorLabel={mtdPrior}
        />
      </SimpleGrid>

      <Text mt={6} mb={3} fontWeight="semibold" fontSize="sm">
        Full-Month Outlook — {outCur} vs {outPrior}
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <CompareCard
          title="Marketing Spend — Projected Month"
          value={strip.marketingSpendProjected?.current}
          prior={strip.marketingSpendProjected?.prior}
          formatValue={formatCompactCurrency}
          currentLabel={outCur}
          priorLabel={outPrior}
          forecast
          tooltip={MARKETING_FORECAST_TOOLTIP}
          helpLink="marketing-spend-month-forecast"
        />
        <CompareCard
          title="Revenue — Projected Month"
          value={strip.revenueProjected?.current}
          prior={strip.revenueProjected?.prior}
          formatValue={formatCompactCurrency}
          currentLabel={outCur}
          priorLabel={outPrior}
          forecast
          tooltip={REVENUE_FORECAST_TOOLTIP}
        />
      </SimpleGrid>
    </Box>
  );
}

export {
  formatCompactCurrency,
  formatCount,
};
